import { useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { generateKit, submitQuizAttempt } from '../../../actions/coachCertificationActions'
import { showGenericError } from '../../../utility/helperFunctions'
import { itemPath, nextItem, previousItem, sectionsPath } from '../../../utility/coachCertification'
import QuizHeader from './QuizHeader'
import QuizQuestion from './QuizQuestion'
import QuizResults from './QuizResults'
import Celebration from './Celebration'

import classes from './QuizView.module.scss'

const QuizView = ({ item, items, certification }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { quiz, final } = item
  const canAttempt = !quiz.passed && (quiz.attemptsLeft === null || quiz.attemptsLeft > 0)
  const [index, setIndex] = useState(0)
  const [picks, setPicks] = useState({})
  const [result, setResult] = useState(quiz.lastAttempt)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const shownResult = isSubmitting ? null : result || (canAttempt ? null : quiz.lastAttempt)
  const [isCelebrating, setIsCelebrating] = useState(false)
  const next = nextItem(items, item)
  const previous = previousItem(items, item)

  const submit = () => {
    setIsSubmitting(true)
    dispatch(submitQuizAttempt({ quizId: quiz.id, answers: picks })).unwrap()
      .then(({ quizAttempt }) => {
        setResult(quizAttempt)
        setIsCelebrating(final && quizAttempt.passed)
        if (final && quizAttempt.passed) { dispatch(generateKit(certification.enrollment.id)).unwrap().catch(showGenericError) }
      })
      .catch(showGenericError)
      .finally(() => setIsSubmitting(false))
  }

  const retake = () => { setPicks({}); setIndex(0); setResult(null) }

  const goPrevious = () => {
    !shownResult && index > 0 ? setIndex(index - 1) : navigate(previous ? itemPath(previous) : sectionsPath(item.certificationId))
  }

  const goNext = () => navigate(next ? itemPath(next) : sectionsPath(item.certificationId))

  if (isCelebrating && result) {
    return <Celebration certification={certification} score={result.score} onClose={() => setIsCelebrating(false)} />
  }

  return (
    <div className={classes.card}>
      <QuizHeader item={item} index={index} showCounter={!shownResult} />

      {shownResult && (
        <QuizResults
          quiz={quiz}
          attempt={shownResult}
          canRetake={!shownResult.passed && canAttempt}
          onRetake={retake}
          onPrevious={goPrevious}
          onNext={goNext}
          hasNext={!!next}
        />
      )}
      {!canAttempt && !quiz.passed && !isSubmitting && <p className={classes.exhausted}>{t('portal.certification.quiz.noAttemptsLeft')}</p>}
      {!shownResult && (canAttempt || isSubmitting) && (
        <QuizQuestion
          question={quiz.questions[index]}
          pick={picks[quiz.questions[index]?.id]}
          isLast={index === quiz.questions.length - 1}
          isSubmitting={isSubmitting}
          onPick={(answerId) => setPicks(current => ({ ...current, [quiz.questions[index].id]: answerId }))}
          onPrevious={goPrevious}
          onNext={() => (index === quiz.questions.length - 1 ? submit() : setIndex(index + 1))}
        />
      )}
    </div>
  )
}

QuizView.propTypes = {
  item: PropTypes.shape({
    certificationId: PropTypes.number.isRequired,
    final: PropTypes.bool.isRequired,
    quiz: PropTypes.shape({
      id: PropTypes.number.isRequired,
      attemptsLeft: PropTypes.number,
      passed: PropTypes.bool.isRequired,
      questions: PropTypes.array.isRequired,
      lastAttempt: PropTypes.object,
    }).isRequired,
  }).isRequired,
  items: PropTypes.array.isRequired,
  certification: PropTypes.object.isRequired,
}

export default QuizView
